import React, { useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

import {
  Container,
  Row,
  Col,
  CardBody,
  Label,
  NavItem,
  NavLink
} from 'reactstrap'
import styled from 'styled-components'

import API from '../utils/API'
import { parseOrders, OrdersRow } from 'utils/ordersHandler'

import OrdersTable from '../components/OrdersTable'
import SideMenu from '../components/SideMenu'
import SearchForm from '../components/SearchForm'

import { Column } from 'models/table.model'
import { RootState } from 'models/redux.model'
import socket from 'utils/socket'

import SelectPharma from '../components/SelectPharma'
import { useIntl } from 'react-intl'

import InfosViewDisabledModal from 'components/InfosViewDisabledModal'

import { faSyncAlt, faFilter } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import Helmet from 'react-helmet'
import { LoaderWrapper } from 'assets/styles/LoaderWrapper'
import { OrdersWrapper } from 'assets/styles/OrdersWrapper'
import { CardWrapper } from 'assets/styles/CardWrapper'
import { ContainerWrapper } from 'assets/styles/ContainerWrapper'
import { applySort } from 'utils/sortHandler'

import FilterModal from 'components/Filter/FilterModal'
import { useLocation } from 'react-router'
import NoResultFound from 'components/Filter/NoResultFound'
import moment, { Moment } from 'moment'
import { parseInt } from 'lodash'
import { history } from 'store'

const loadingIcon = 'images/loading-gif-phacil.svg'
const blurOrder = 'images/blurOrder.png'

/**
 * Ajoute des commandes à la liste sans doublon (clé orderId + pharmacyOrderId),
 * triées par date de création décroissante
 */
const mergeOrders = (existing: OrdersRow[], incoming: OrdersRow[]): OrdersRow[] => {
  const existingKeys = new Set(existing.map(order => `${order.orderId}t${order.pharmacyOrderId}`))
  const newOrders = incoming.filter(order => !existingKeys.has(`${order.orderId}t${order.pharmacyOrderId}`))
  return [...existing, ...newOrders].sort((orderA, orderB) => {
    return orderB.dateOrder.valueOf() - orderA.dateOrder.valueOf()
  })
}

const Orders = (props: any): JSX.Element => {
  // Pharmacy list from Redux
  const pharmacies = useSelector((state: RootState) => state.user.pharmacies)
  const selectedPharma = useSelector(
    (state: RootState) => state.user.selectedPharma
  )
  const { formatMessage, locale } = useIntl()
  const f = (id: any): string => formatMessage({ id })
  const [data, setData] = useState<OrdersRow[]>([]) // Array of examples
  const [filteredRows, setFilteredRows] = useState<OrdersRow[]>([]) // Array of examples
  const [currentSearchValue, setCurSearchValue] = useState<string>('')
  const [sortCriteria, setSortCriteria] = useState<{
    direction: 'DESC' | 'ASC'
    accessor: string
  } | null>({
    direction: 'DESC',
    accessor: 'dateOrder'
  })

  // Nouveaux états pour la pagination progressive
  const [nextIndex, setNextIndex] = useState<number>(0)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [isFullyLoaded, setIsFullyLoaded] = useState<boolean>(false)
  const [loadingMore, setLoadingMore] = useState<boolean>(false)
  const [autoLoadingTriggered, setAutoLoadingTriggered] = useState<boolean>(false)
  const loadingPagesRef = useRef<boolean>(false) // chargement de page en cours (cf. loadPagesUpTo)
  const FILTER_THRESHOLD = 10 // Seuil pour déclencher le chargement automatique

  const pageSize = 50
  const [isLoading, setIsLoading] = useState(true)

  const [filter, setFilter] = useState<number>(0) // [Validée, En attente, Annulée]

  const motifsByLanguage = [f('all'), f('confirmed'), f('waiting'), f('canceled')]

  const [activeTab, setActiveTab] = useState('0')

  const [openModalDisabled, setOpenModalDisabled] = useState(true)
  const toggle = (): void => setOpenModalDisabled(!openModalDisabled)

  const [isOpenFilter, setIsOpenFilter] = useState(false)
  const toggleFilter = (): void => setIsOpenFilter(!isOpenFilter)

  const [openDate, setOpenDate] = useState(true)

  const [typeDate, setTypeDate] = useState<number>(0)
  const [afterDate, setAfterDate] = useState<Moment>(moment())
  const [startDate, setStartDate] = useState<Moment>(moment())
  const [endDate, setEndDate] = useState<Moment>(moment())
  const [equalDate, setEqualDate] = useState<Moment>(moment())
  const [beforeDate, setBeforeDate] = useState<Moment>(moment())

  const search = useLocation().search
  // Paramètres de la fiche commande ouverte (?order= / ?chat=) : ne doivent pas recharger la liste
  const listSearchParams = new URLSearchParams(search)
  listSearchParams.delete('order')
  listSearchParams.delete('chat')
  const listSearch = listSearchParams.toString()
  const linkedOrderId = new URLSearchParams(search).get('order')

  const isMultipharmaAccount = useSelector((state: RootState) => state.user.isMultipharmaAccount)

  // Modes de pagination :
  // - "lazy" : pagination serveur page par page (aucun filtre/recherche/tri custom actif)
  // - "full" : chargement complet en arrière-plan puis pagination client
  //   (recherche/filtre/tri se font côté client, ils ont besoin de tout le jeu de données)
  const isDateFilterActive = new URLSearchParams(search).get('start') != null || new URLSearchParams(search).get('end') != null
  const isCustomSort = !(sortCriteria != null && sortCriteria.accessor === 'dateOrder' && sortCriteria.direction === 'DESC')

  // Recherche par numéro de commande : une valeur entièrement numérique est demandée au serveur
  // (?orderId=) au lieu d'être filtrée côté client, la commande n'étant pas forcément déjà chargée
  const orderIdSearch = /^\d+$/.test(currentSearchValue.trim()) ? parseInt(currentSearchValue.trim()) : null
  const isOrderIdSearch = orderIdSearch != null
  const [orderIdResults, setOrderIdResults] = useState<OrdersRow[]>([])

  const isFullLoadMode = (currentSearchValue !== '' && !isOrderIdSearch) || filter !== 0 || isDateFilterActive || isCustomSort
  const isServerPagination = !isFullLoadMode && !isOrderIdSearch
  const serverPageCount = Math.max(1, Math.ceil(totalCount / pageSize))

  // Configuration for the Orders table

  const columns: Column[] = [
    {
      Header: f('nbPhacilOrder'),
      // TODO PLACEHOLDER 1234 instead of order_id
      accessor: 'orderId',
      filtrable: true
    },
    {
      Header: f('status'),
      accessor: 'state',
      filtrable: true
    },
    {
      Header: f('orderType2'),
      // TODO PLACEHOLDER 1234 instead of order_id
      accessor: 'type',
      filtrable: true
    },
    {
      Header: f('orderDate'),
      accessor: 'date',
      sortable: true,
      sortAccessor: 'dateOrder'  // Use dateOrder for sorting but display date
    },
    {
      Header: f('price2'),
      accessor: 'costsDetails'
    },
    {
      Header: f('deliveryType'),
      accessor: 'deliveryMode'
    },
    {
      Header: f('client2'),
      accessor: 'customerName'
    },
    {
      Header: f('monitoringNb'),
      accessor: 'tracking_code'
    }
    // {
    //     Header: 'Documents',
    //     accessor: 'file',
    // }
  ]

  // Removed socket listener for 'receive_id' - we now rely on direct REST calls
  // to reload data after updates instead of socket events
  // useEffect(() => {
  //   if (selectedPharma.pharmacy_id !== -1) {
  //     const handler = (id: number) => {
  //       if (id === selectedPharma.pharmacy_id) {
  //         getData(selectedPharma.pharmacy_id, null, null, true).catch(err => console.log(err))
  //       }
  //     }
  //     socket.on('receive_id', handler)
  //     return () => { socket.off('receive_id', handler) }
  //   }
  //   return undefined
  // }, [selectedPharma])

  /**
   * Filters the rows according to the current search value (filter on Name or EAN code)
   */
  const applyFilters = (): void => {
    let newData = data
    const searchParams = new URLSearchParams(window.location.search)
    const isChangingToAllFilter = filter === 0 && searchParams.has('status')

    // Appliquer le filtre par statut
    if (filter !== 0) {
      searchParams.set('status', filter.toString())
      history.replace({ pathname: '/orders', search: searchParams.toString() })

      newData = data.filter(order => {
        let status = -1

        if (order.state > 1 && order.state < 6) status = 1
        else if (order.state <= 1) status = 2
        else if (order.state >= 6) status = 3

        return status === filter
      })

      // Vérifier si le filtre par statut a peu de résultats
      if (newData.length < FILTER_THRESHOLD && hasMore && !loadingMore && !autoLoadingTriggered) {
        console.log('Déclenchement auto-chargement par filtre statut');
        loadAllData()
        // On continue quand même à filtrer avec ce qu'on a déjà
      }
    } else if (!isChangingToAllFilter) {
      // Ne pas modifier l'URL si on est en train de passer au filtre "All"
      // pour éviter de déclencher le rechargement dans le useEffect([search])
      searchParams.delete('status')
      history.replace({ search: searchParams.toString() })
    }

    // Appliquer la recherche textuelle
    let filteredResults = newData

    if (isOrderIdSearch) {
      // Résultat exact renvoyé par le serveur (cf. useEffect [orderIdSearch])
      filteredResults = orderIdResults
    } else if (currentSearchValue != null && currentSearchValue !== '') {
      filteredResults = newData.filter(cart => {
        if (cart.orderId?.toString().includes(currentSearchValue.trim())) return true

        if (cart?.customerName.toUpperCase().includes(currentSearchValue.toUpperCase())) return true

        if (cart.products != null) {
          for (let i = 0; i < cart.products.length; i++) {
            if (cart?.products?.[i]?.EAN_code?.toString()
              .toUpperCase()
              .includes(currentSearchValue.toUpperCase())
            ) { return true }
          }
        }

        return false
      })

      // Vérifier si la recherche textuelle a peu de résultats
      if (filteredResults.length < FILTER_THRESHOLD && hasMore && !loadingMore && !autoLoadingTriggered) {
        console.log('Déclenchement auto-chargement par recherche textuelle');
        loadAllData()
        // On continue quand même à filtrer avec ce qu'on a déjà
      }
    }

    setFilteredRows([...filteredResults])
  }

  useEffect(() => {
    const searchStart = new URLSearchParams(search).get('start')
    const searchEnd = new URLSearchParams(search).get('end')
    const searchStatus = new URLSearchParams(search).get('status')
    const previousUrl = sessionStorage.getItem('previousUrl') || ''
    const currentUrl = window.location.search

    // Stocker l'URL actuelle pour la prochaine comparaison
    sessionStorage.setItem('previousUrl', currentUrl)

    // Détecter si seul le status a été retiré (passage d'un filtre à "All")
    const previousParams = new URLSearchParams(previousUrl)
    const currentParams = new URLSearchParams(currentUrl)
    const wasStatusRemoved = previousParams.has('status') && !currentParams.has('status') &&
      previousParams.get('start') === currentParams.get('start') &&
      previousParams.get('end') === currentParams.get('end')

    if (searchStatus == null && !wasStatusRemoved) {
      // Réinitialiser les données lors du changement de filtre par date
      // ou lors du premier chargement
      getData(selectedPharma.pharmacy_id, searchStart, searchEnd, true).catch((err) => { console.log(err) })
      setAutoLoadingTriggered(false)
    }
  }, [listSearch])

  /** CRUD FUNCTIONS */
  const getData = async (pharmaId: number, start?: string | null, end?: string | null, reset: boolean = false): Promise<void> => {
    // Configurer l'état de chargement
    if (reset) {
      setIsLoading(true)
      setNextIndex(0)
      setData([])
      setHasMore(true)
      setIsFullyLoaded(false)
      setAutoLoadingTriggered(false)
    } else if (nextIndex === 0) {
      setIsLoading(true)
    } else {
      setLoadingMore(true)
    }

    const timestampStart = new URLSearchParams(search).get('start')
    const timestampEnd = new URLSearchParams(search).get('end')

    try {
      // Vérifier que la pharmacie est valide
      if (pharmaId == null && selectedPharma != null) {
        pharmaId = selectedPharma.pharmacy_id
      }

      if (pharmaId === -1) {
        // Pas de pharmacie valide, arrêter le chargement
        setIsLoading(false)
        setLoadingMore(false)
        return
      }

      // Déterminer l'index à utiliser pour cette requête
      // Si reset est vrai, on utilise 0, sinon on utilise la valeur actuelle de nextIndex
      const indexToUse = reset ? 0 : nextIndex

      // Construire l'URL avec les paramètres de pagination
      let url = `/pharmacist/pharmacy-orders/${pharmaId}?index=${indexToUse}&limit=200`

      // Ajouter les paramètres de filtrage par date si nécessaire
      if (start != null || end != null || timestampStart != null || timestampEnd != null) {
        url += `&start=${start || timestampStart}&end=${end || timestampEnd}`
      }

      console.log(`Chargement des données: ${url}`)
      const examplesResponse = await API.get(url)

      // Récupérer les nouvelles commandes
      let newOrders = parseOrders(
        examplesResponse.data.responsePara,
        examplesResponse.data.responsePrescription,
        examplesResponse.data.responseParaAndPrescription,
        locale
      )

      // Traiter les informations de pagination
      const newNextIndex = examplesResponse.data.pagination?.nextIndex || 0
      const newHasMore = examplesResponse.data.pagination?.hasMore || false
      const newTotalCount = examplesResponse.data.pagination?.totalCount || 0

      // Mise à jour des données
      setData(prevData => {
        // Si c'est un reset, on utilise uniquement les nouvelles données
        // Sinon, on ajoute les nouvelles données aux données existantes (sans doublon, triées par date)
        return mergeOrders(reset ? [] : prevData, newOrders)
      })

      // Mise à jour des informations de pagination après avoir mis à jour les données
      setNextIndex(newNextIndex)
      setHasMore(newHasMore)
      setTotalCount(newTotalCount)

      // Si plus de données à charger, marquer comme entièrement chargé
      if (!newHasMore) {
        setIsFullyLoaded(true)
        setAutoLoadingTriggered(false)
      } else if (reset) {
        // Si c'est un reset mais qu'il y a plus de données, on n'est pas entièrement chargé
        setIsFullyLoaded(false)
      }
    } catch (error) {
      console.error("Erreur lors du chargement des données:", error)
    } finally {
      // Toujours nettoyer les états de chargement
      setIsLoading(false)
      setLoadingMore(false)
    }
  }

  /**
   * Charge le prochain lot de commandes
   */
  const loadNextBatch = (): void => {
    if (hasMore && !loadingMore && !isLoading) {
      getData(selectedPharma.pharmacy_id)
    }
  }

  /**
   * Charge les lots de commandes nécessaires (chunk by chunk) pour couvrir la page demandée.
   * Utilisé en mode pagination paresseuse (sans filtre/recherche/tri custom).
   */
  const loadPagesUpTo = (targetPage: number): void => {
    const targetCount = (targetPage + 1) * pageSize
    // Le ref évite qu'une rafale de clics (les états React n'étant pas encore à jour)
    // lance plusieurs chargements concurrents sur le même index
    if (data.length >= targetCount || !hasMore || loadingMore || loadingPagesRef.current) return

    void (async () => {
      loadingPagesRef.current = true
      setLoadingMore(true)
      try {
        let currentIndex = nextIndex
        let acc: OrdersRow[] = [...data]
        let more: boolean = hasMore

        while (more && acc.length < targetCount) {
          const url = `/pharmacist/pharmacy-orders/${selectedPharma.pharmacy_id}?index=${currentIndex}&limit=200`
          const response = await API.get(url)
          const newOrders = parseOrders(
            response.data.responsePara,
            response.data.responsePrescription,
            response.data.responseParaAndPrescription,
            locale
          )
          acc = mergeOrders(acc, newOrders)

          const pagination = response.data.pagination
          if (pagination != null) setTotalCount(pagination.totalCount)
          if (pagination != null && pagination.hasMore && pagination.nextIndex > currentIndex) {
            currentIndex = pagination.nextIndex
            more = true
          } else {
            // Plus rien à charger (ou index qui n'avance pas) : on s'arrête
            more = false
            setIsFullyLoaded(true)
          }
          setHasMore(more)
          setNextIndex(currentIndex)
        }

        setData(acc)
      } catch (error) {
        console.error('Erreur lors du chargement de la page:', error)
      } finally {
        loadingPagesRef.current = false
        setLoadingMore(false)
      }
    })()
  }

  /**
   * Force le rechargement complet des données depuis le début
   * Utilisé après une mise à jour pour éviter les doublons
   */
  const reloadData = (): void => {
    getData(selectedPharma.pharmacy_id, null, null, true).catch(err => console.log(err))
  }

  /**
   * Charge toutes les commandes en continu
   * 
   * Cette implémentation évite de s'appuyer sur les états React dans la boucle
   * pour éviter les problèmes d'asynchronicité des mises à jour d'état.
   */
  const loadAllData = async (): Promise<void> => {
    if (!hasMore || autoLoadingTriggered) return

    // Marquer le début du chargement
    setAutoLoadingTriggered(true)
    setLoadingMore(true)

    try {
      // Variables locales pour suivre l'état pendant les requêtes
      let currentIndex = nextIndex
      let shouldContinue = true
      let allOrders: OrdersRow[] = [...data]

      // Boucle qui continue tant qu'il y a plus de données à charger
      while (shouldContinue) {
        // Récupérer les paramètres de filtrage
        const timestampStart = new URLSearchParams(search).get('start')
        const timestampEnd = new URLSearchParams(search).get('end')

        // Construire l'URL avec les paramètres de pagination
        let url = `/pharmacist/pharmacy-orders/${selectedPharma.pharmacy_id}?index=${currentIndex}&limit=200`

        // Ajouter les paramètres de filtrage par date si nécessaire
        if (timestampStart != null || timestampEnd != null) {
          url += `&start=${timestampStart}&end=${timestampEnd}`
        }

        try {
          // Effectuer la requête API
          console.log(`Chargement des données à partir de l'index ${currentIndex}`)
          const response = await API.get(url)

          // Traiter les données reçues
          const newOrders = parseOrders(
            response.data.responsePara,
            response.data.responsePrescription,
            response.data.responseParaAndPrescription,
            locale
          )

          // Mettre à jour la collection locale (sans doublon, triée par date)
          allOrders = mergeOrders(allOrders, newOrders)

          // Vérifier s'il y a plus de données à charger
          if (response.data.pagination) {
            if (!response.data.pagination.hasMore) {
              // Plus de données à charger
              shouldContinue = false

              // Mettre à jour les états une fois à la fin
              setIsFullyLoaded(true)
              setHasMore(false)
              setTotalCount(response.data.pagination.totalCount)
            } else {
              // Mettre à jour l'index pour la prochaine itération
              currentIndex = response.data.pagination.nextIndex

              // Mettre à jour l'état totalCount
              setTotalCount(response.data.pagination.totalCount)
            }
          } else {
            // Si l'API ne renvoie pas d'informations de pagination, arrêter
            shouldContinue = false
            setIsFullyLoaded(true)
            setHasMore(false)
          }

          // Mettre à jour périodiquement les données affichées pour montrer la progression
          setData(allOrders)

          // Mettre à jour nextIndex après chaque chargement réussi
          setNextIndex(currentIndex)

          // Pause courte pour éviter de surcharger le serveur
          await new Promise(resolve => setTimeout(resolve, 300))

        } catch (error) {
          console.error("Erreur lors du chargement automatique:", error)
          shouldContinue = false
        }
      }

      // Mettre à jour une dernière fois pour s'assurer que toutes les données sont affichées
      setData(allOrders)

    } catch (error) {
      console.error("Erreur générale dans loadAllData:", error)
    } finally {
      // Toujours nettoyer les états de chargement
      setLoadingMore(false)
      setAutoLoadingTriggered(false)
    }
  };
  /**
   * OTHER EVENTS HANDLERS
   */
  useEffect(() => {
    if (selectedPharma != null) {
      socket.emit('join_room', `admin_pharma_${selectedPharma.pharmacy_id}`)
      // Réinitialiser les données lors du changement de pharmacie
      getData(selectedPharma.pharmacy_id, null, null, true).catch(err => console.log(err))
      setIsOpenFilter(false)
      setAutoLoadingTriggered(false)
    }
  }, [selectedPharma])

  // Signal temps réel : un nouveau message patient marque la commande comme non lue
  // dans la liste, sans rechargement (le backend émet vers admin_pharma_<pharmacy_id>)
  useEffect(() => {
    const handler = (payload: { orderId: number, pharmacyOrderId: number }): void => {
      setData(prev => prev.map(order =>
        (order.orderId === payload.orderId && order.pharmacyOrderId === payload.pharmacyOrderId)
          ? { ...order, hasUnreadPatientMessage: true }
          : order
      ))
    }
    socket.on('new_patient_message', handler)
    return () => { socket.off('new_patient_message', handler) }
  }, [])

  // Recherche par N° de commande : une saisie numérique déclenche une recherche
  // directe côté serveur (la commande peut ne pas être encore chargée par la
  // pagination). Le résultat est fusionné dans `data`, puis filtré par applyFilters.
  useEffect(() => {
    const searchedOrderId = currentSearchValue.trim()
    if (!/^\d+$/.test(searchedOrderId) || selectedPharma.pharmacy_id === -1) return undefined

    const timeout = setTimeout(() => {
      const timestampStart = new URLSearchParams(search).get('start')
      const timestampEnd = new URLSearchParams(search).get('end')
      let url = `/pharmacist/pharmacy-orders/${selectedPharma.pharmacy_id}?orderId=${searchedOrderId}`
      if (timestampStart != null || timestampEnd != null) {
        url += `&start=${timestampStart}&end=${timestampEnd}`
      }

      API.get(url)
        .then(response => {
          const foundOrders = parseOrders(
            response.data.responsePara,
            response.data.responsePrescription,
            response.data.responseParaAndPrescription,
            locale
          )
          if (foundOrders.length === 0) return

          setData(prevData => mergeOrders(prevData, foundOrders))
        })
        .catch(err => console.error('Erreur lors de la recherche par N° de commande:', err))
    }, 400)

    return () => clearTimeout(timeout)
  }, [currentSearchValue, selectedPharma])

  // Lien direct vers une commande (?order=) : elle n'est pas forcément dans les lots déjà chargés
  useEffect(() => {
    if (isLoading || linkedOrderId == null || !/^\d+$/.test(linkedOrderId)) return
    if (data.some(order => order.orderId === parseInt(linkedOrderId))) return

    API.get(`/pharmacist/pharmacy-orders/${selectedPharma.pharmacy_id}?orderId=${linkedOrderId}`)
      .then(response => {
        const foundOrders = parseOrders(
          response.data.responsePara,
          response.data.responsePrescription,
          response.data.responseParaAndPrescription,
          locale
        )
        if (foundOrders.length > 0) setData(prevData => mergeOrders(prevData, foundOrders))
      })
      .catch(err => console.error('Erreur lors du chargement de la commande liée:', err))
  }, [isLoading, linkedOrderId, selectedPharma])

  useEffect(() => {
    applyFilters()
  }, [currentSearchValue, data, filter, orderIdResults])

  // Recherche par numéro de commande : appel serveur avec ?orderId=, indépendant de la pagination
  useEffect(() => {
    if (orderIdSearch == null) {
      setOrderIdResults([])
      return undefined
    }
    let cancelled = false
    const timeout = setTimeout(() => {
      API.get(`/pharmacist/pharmacy-orders/${selectedPharma.pharmacy_id}?index=0&limit=200&orderId=${orderIdSearch}`)
        .then(response => {
          if (cancelled) return
          setOrderIdResults(parseOrders(
            response.data.responsePara,
            response.data.responsePrescription,
            response.data.responseParaAndPrescription,
            locale
          ))
        })
        .catch(err => {
          console.error('Erreur lors de la recherche par numéro de commande:', err)
          if (!cancelled) setOrderIdResults([])
        })
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timeout)
    }
  }, [orderIdSearch, selectedPharma])

  useEffect(() => {
    applySort(data, setData, sortCriteria)
  }, [sortCriteria])

  // En mode filtre/recherche/tri custom, charger tout le jeu de données en arrière-plan
  // (la recherche, le filtre et le tri s'appliquent côté client sur l'ensemble)
  useEffect(() => {
    if (isFullLoadMode && hasMore && !loadingMore && !autoLoadingTriggered) {
      loadAllData().catch(err => console.log(err))
    }
  }, [isFullLoadMode])

  useEffect(() => {
    if (isLoading) {
      if (data.length > 0) {
        setData(prevState => {
          return prevState.sort((a: any, b: any) => {
            // Sort by date of creation (timestamp) instead of order_id
            return b.dateOrder.valueOf() - a.dateOrder.valueOf()
          })
        })
        setIsLoading(false)
      }
    }
  }, [data])

  // useEffect(() => {
  //   if (!isLoading) setFilteredRows([...data])
  // }, [isLoading])

  useEffect(() => {
    const start = new URLSearchParams(search).get('start')
    const end = new URLSearchParams(search).get('end')
    const status = new URLSearchParams(search).get('status')

    if ((start != null && end == null)) {
      setAfterDate(moment(parseInt(start) * 1000))
      setTypeDate(2)
    } else if ((start == null && end != null)) {
      setBeforeDate(moment(parseInt(end) * 1000))
      setTypeDate(3)
    } else if ((start != null && end != null)) {
      const tmp = moment(new Date(parseInt(start) * 1000)).format('YYYY-MM-DD')
      const tmpEnd = moment(new Date(parseInt(end) * 1000)).format('YYYY-MM-DD')
      if (moment(tmp).isSame(tmpEnd)) {
        setTypeDate(1)
        setEqualDate(moment(parseInt(start) * 1000))
      } else {
        setTypeDate(0)
        setStartDate(moment(parseInt(start) * 1000))
        setEndDate(moment(parseInt(end) * 1000))
      }
    }

    if (status != null) {
      setFilter(parseInt(status))
      setActiveTab(status)
    }
  }, [selectedPharma])

  return (
    <OrdersWrapper property='order' className='dataContainer'>
      <Helmet>
        <title>{f('ordersMetaTitle')}</title>
        <link rel='canonical' href='https://www.phacil.app/' />
        <meta name='description' content={f('ordersMetaDescription')} />
        <link rel='alternate' hrefLang='fr' href='https://www.phacil.app/' />
      </Helmet>
      <SideMenu parent='Orders' />
      <ContainerWrapper>
        <Container fluid className={(selectedPharma.pharmacy.pharmacy_menu?.orders === 1 && !isMultipharmaAccount) ? 'blur' : ''}>
          {
            (selectedPharma.pharmacy.pharmacy_menu?.orders === 1 && !isMultipharmaAccount)
              ? <img src={blurOrder} alt='' />
              : (
                <>
                  <Row className='headerTitle'>
                    <h2>{f('orders')}</h2>
                    {selectedPharma && (
                      <SelectPharma pharmacies={pharmacies} />
                    )}
                    <StyledFontAwesomeIcon
                      icon={faSyncAlt}
                      onClick={() => {
                        // Réinitialiser et recharger les données avec reset=true
                        if (search === '') {
                          getData(selectedPharma.pharmacy_id, null, null, true).catch(err => console.log(err))
                        } else {
                          history.push({ pathname: '/orders' })
                        }
                      }}
                    />
                  </Row>
                  <Row>
                    <Col xs='12'>
                      <CardWrapper order>
                        {isLoading ? (
                          <LoaderWrapper>
                            <h5>{f('loadingOrders')}</h5>
                            <img src={loadingIcon} alt='loading' width='35' />
                          </LoaderWrapper>
                        ) : (
                          <CardBody>
                            <FiltersAboveTable>
                              <BorderBottomCol className='nav' lg='12'>
                                {motifsByLanguage.map((motifByLanguage: string, index: number) => (
                                  <NavItem key={index}>
                                    <NavLink
                                      className={activeTab === index.toString() ? 'active' : ''}
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => {
                                        const previousFilter = filter;
                                        setFilter(index)
                                        setActiveTab(index.toString())

                                        // Ne pas enregistrer le filtre dans l'URL si on revient à "All"
                                        if (index === 0) {
                                          const searchParams = new URLSearchParams(window.location.search);
                                          searchParams.delete('status');
                                          history.replace({ pathname: '/orders', search: searchParams.toString() });
                                          console.log('Passage au filtre All sans rechargement');
                                        }
                                      }}
                                    >
                                      {motifByLanguage}
                                    </NavLink>
                                  </NavItem>
                                ))}

                                <SearchbarWrapper>
                                  <button
                                    id='popoverFilter' type='submit' className='btn btnFilter mr-2'
                                    onClick={() => { setOpenDate(true) }}
                                  >
                                    <FontAwesomeIcon
                                      style={{
                                        cursor: 'pointer',
                                        marginRight: '5px'
                                      }}
                                      icon={faFilter} size='xs'

                                    />

                                    {f('filter')}
                                  </button>
                                  {f('search')}
                                  <SearchForm
                                    onSearch={applyFilters}
                                    setInputValue={newValue =>
                                      setCurSearchValue(newValue)}
                                    inputValue={currentSearchValue}
                                    placeholder={f('nameCodeEanOrderId')}
                                  />
                                </SearchbarWrapper>
                              </BorderBottomCol>
                            </FiltersAboveTable>
                            <FilterModal
                              isOpenFilter={isOpenFilter}
                              openDate={openDate}
                              setOpenDate={setOpenDate}
                              setIsOpenFilter={setIsOpenFilter}
                              toggleFilter={toggleFilter}
                              typeDate={typeDate}
                              setTypeDate={setTypeDate}
                              afterDate={afterDate}
                              setAfterDate={setAfterDate}
                              startDate={startDate}
                              setStartDate={setStartDate}
                              endDate={endDate}
                              setEndDate={setEndDate}
                              equalDate={equalDate}
                              setEqualDate={setEqualDate}
                              beforeDate={beforeDate}
                              setBeforeDate={setBeforeDate}

                            />
                            {data.length > 0
                              ? (
                                <OrdersTable
                                  sortCriteria={sortCriteria}
                                  setSortCriteria={setSortCriteria}
                                  columns={columns}
                                  data={filteredRows}
                                  pageSize={pageSize}
                                  searchValue={currentSearchValue}
                                  getData={reloadData}
                                  setData={setData}
                                  allData={data}
                                  // Nouvelles propriétés pour la pagination progressive
                                  hasMore={hasMore}
                                  loadingMore={loadingMore}
                                  isFullyLoaded={isFullyLoaded}
                                  loadNextBatch={loadNextBatch}
                                  loadAllData={loadAllData}
                                  autoLoadingTriggered={autoLoadingTriggered}
                                  totalCount={totalCount}
                                  activeFilter={filter}
                                  // Pagination paresseuse (serveur) hors filtre/recherche/tri
                                  serverPageCount={isServerPagination ? serverPageCount : undefined}
                                  onRequestPage={isServerPagination ? loadPagesUpTo : undefined}
                                />
                              ) : <NoResultFound />}

                          </CardBody>
                        )}
                      </CardWrapper>
                    </Col>
                  </Row>
                </>
              )
          }

        </Container>
      </ContainerWrapper>
      {
        selectedPharma.pharmacy.pharmacy_menu?.orders === 1 && !isMultipharmaAccount &&
        <InfosViewDisabledModal
          isOpen={openModalDisabled}
          toggle={toggle}
        />
      }
    </OrdersWrapper>
  )
}

const FiltersAboveTable = styled(Row)`
  color: var(--texte-table);
  font-weight: 700;
  font-size: 0.9rem;

  label select {
    margin: 0 0.5rem;
  }
  label form {
    margin-left: 0.5rem;
  }
  .nav {
    margin-left: 1rem;
  }
`
const SearchbarWrapper = styled(Label)`
  display: flex;
  justify-content: flex-end;
  flex: auto;
  margin-bottom: unset;
  input {
    margin-right: 0;
  }
`
const BorderBottomCol = styled(Col)`
  border-bottom: 1px solid #dee2e6;
  padding: 0;
  max-width: 97%;
  a:hover,
  span:hover {
    filter: brightness(80%);
  }
`
const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
  margin-top: 8px;
  margin-left: 10px;
  cursor: pointer;
`

export default Orders
